import { promises as fs } from "node:fs";
import os from "node:os";
import path from "node:path";
import { parse as parseToml, stringify as stringifyToml } from "smol-toml";
import {
  type CanonicalMcpServer,
  type ClaudeMcpEntry,
  type CodexMcpEntry,
  type OpenCodeMcpEntry,
  canonicalMcpServerSchema,
  mergeIntoClaudeConfig,
  mergeIntoCodexConfig,
  mergeIntoOpenCodeConfig,
  toClaudeMcpEntry,
  toCodexMcpEntry,
  toOpenCodeMcpEntry,
} from "./compile.js";

export interface VaultReadResult {
  servers: Record<string, CanonicalMcpServer>;
  invalid: { file: string; reason: string }[];
}

/** Read + validate every `<name>.json` in the vault dir. Invalid files are skipped, not fatal. */
export async function readVault(vaultDir: string): Promise<VaultReadResult> {
  const servers: Record<string, CanonicalMcpServer> = {};
  const invalid: { file: string; reason: string }[] = [];
  let files: string[];
  try {
    files = await fs.readdir(vaultDir);
  } catch {
    return { servers, invalid };
  }
  for (const file of files) {
    if (!file.endsWith(".json")) {
      continue;
    }
    const name = file.slice(0, -".json".length);
    let raw: unknown;
    try {
      raw = JSON.parse(await fs.readFile(path.join(vaultDir, file), "utf8"));
    } catch (error) {
      invalid.push({ file, reason: `invalid JSON: ${String(error)}` });
      continue;
    }
    const parsed = canonicalMcpServerSchema.safeParse(raw);
    if (!parsed.success) {
      invalid.push({ file, reason: parsed.error.issues.map((issue) => issue.message).join("; ") });
      continue;
    }
    servers[name] = parsed.data;
  }
  return { servers, invalid };
}

interface ManagedStore {
  claude?: string[];
  opencode?: string[];
  codex?: string[];
}

async function readManagedStore(storePath: string): Promise<ManagedStore> {
  try {
    const parsed: unknown = JSON.parse(await fs.readFile(storePath, "utf8"));
    return typeof parsed === "object" && parsed !== null ? (parsed as ManagedStore) : {};
  } catch {
    return {};
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

async function readJsonObject(configPath: string): Promise<Record<string, unknown> | null> {
  let text: string;
  try {
    text = await fs.readFile(configPath, "utf8");
  } catch (error) {
    if ((error as NodeJS.ErrnoException).code === "ENOENT") {
      return null;
    }
    throw error;
  }
  const parsed: unknown = JSON.parse(text);
  return isRecord(parsed) ? parsed : {};
}

async function atomicWriteJson(configPath: string, value: unknown): Promise<void> {
  const tmp = `${configPath}.appostle-tmp-${process.pid}`;
  await fs.writeFile(tmp, `${JSON.stringify(value, null, 2)}\n`, "utf8");
  await fs.rename(tmp, configPath);
}

async function readTomlObject(configPath: string): Promise<Record<string, unknown> | null> {
  let text: string;
  try {
    text = await fs.readFile(configPath, "utf8");
  } catch (error) {
    if ((error as NodeJS.ErrnoException).code === "ENOENT") {
      return null;
    }
    throw error;
  }
  const parsed: unknown = parseToml(text);
  return isRecord(parsed) ? parsed : {};
}

async function atomicWriteToml(configPath: string, value: Record<string, unknown>): Promise<void> {
  const tmp = `${configPath}.appostle-tmp-${process.pid}`;
  await fs.writeFile(tmp, stringifyToml(value), "utf8");
  await fs.rename(tmp, configPath);
}

export interface ProviderApplyResult {
  provider: "claude" | "opencode" | "codex";
  configPath: string;
  detected: boolean;
  added: string[];
  updated: string[];
  removed: string[];
  skipped: { name: string; reason: string }[];
  wrote: boolean;
  error?: string;
}

interface Diff {
  added: string[];
  updated: string[];
  removed: string[];
}

function diffSection(
  existing: Record<string, unknown>,
  entries: Record<string, unknown>,
  previouslyOwned: string[],
): Diff {
  const added: string[] = [];
  const updated: string[] = [];
  for (const [name, entry] of Object.entries(entries)) {
    if (!(name in existing)) {
      added.push(name);
    } else if (JSON.stringify(existing[name]) !== JSON.stringify(entry)) {
      updated.push(name);
    }
  }
  const removed = previouslyOwned.filter((name) => !(name in entries) && name in existing);
  return { added, updated, removed };
}

function sectionOf(config: Record<string, unknown>, key: string): Record<string, unknown> {
  return isRecord(config[key]) ? config[key] : {};
}

export interface ApplyMcpVaultOptions {
  vaultDir?: string;
  managedStorePath?: string;
  homeDir?: string;
  env?: Record<string, string | undefined>;
  /** Which providers to process. Defaults to all. */
  providers?: Array<"claude" | "opencode" | "codex">;
  dryRun: boolean;
}

export interface ApplyMcpVaultResult {
  vault: VaultReadResult;
  providers: ProviderApplyResult[];
}

/**
 * Compile the canonical MCP vault into each detected provider's native config via
 * surgical merge. `dryRun` computes the diff without touching any file.
 */
export async function applyMcpVault(options: ApplyMcpVaultOptions): Promise<ApplyMcpVaultResult> {
  const home = options.homeDir ?? os.homedir();
  const vaultDir = options.vaultDir ?? path.join(home, ".appostle", "mcp-servers");
  const managedStorePath =
    options.managedStorePath ?? path.join(home, ".appostle", "mcp-vault-managed.json");
  const env = options.env ?? process.env;

  const vault = await readVault(vaultDir);
  const managed = await readManagedStore(managedStorePath);
  const providers: ProviderApplyResult[] = [];
  const nextManaged: ManagedStore = { ...managed };
  const selected = options.providers ?? ["claude", "opencode", "codex"];

  // --- Claude (~/.claude.json) ---
  if (selected.includes("claude")) {
    providers.push(
      await applyToProvider({
        provider: "claude",
        configPath: path.join(home, ".claude.json"),
        mcpKey: "mcpServers",
        previouslyOwned: managed.claude ?? [],
        dryRun: options.dryRun,
        readConfig: readJsonObject,
        writeConfig: atomicWriteJson,
        buildEntries: () => {
          // Every catalog server lands, even one whose {env:VAR} can't be
          // resolved here — compile leaves the literal placeholder in place and
          // it surfaces as a failed server the user wires up later. We do not
          // skip on missing env (product intent: they land, they exist).
          const entries: Record<string, ClaudeMcpEntry> = {};
          for (const [name, server] of Object.entries(vault.servers)) {
            entries[name] = toClaudeMcpEntry(name, server, env);
          }
          return { entries, skipped: [] };
        },
        merge: (config, entries) =>
          mergeIntoClaudeConfig({ config, entries, previouslyOwned: managed.claude ?? [] }),
        onOwned: (owned) => {
          nextManaged.claude = owned;
        },
      }),
    );
  }

  // --- OpenCode (~/.config/opencode/opencode.jsonc) ---
  if (selected.includes("opencode")) {
    providers.push(
      await applyToProvider({
        provider: "opencode",
        configPath: path.join(home, ".config", "opencode", "opencode.jsonc"),
        mcpKey: "mcp",
        previouslyOwned: managed.opencode ?? [],
        dryRun: options.dryRun,
        readConfig: readJsonObject,
        writeConfig: atomicWriteJson,
        buildEntries: () => {
          // Land every server. OpenCode resolves {env:VAR} natively at runtime,
          // so the placeholder is passed through; if the var is unset the server
          // simply fails to connect until the user sets it.
          const entries: Record<string, OpenCodeMcpEntry> = {};
          for (const [name, server] of Object.entries(vault.servers)) {
            entries[name] = toOpenCodeMcpEntry(name, server);
          }
          return { entries, skipped: [] };
        },
        merge: (config, entries) =>
          mergeIntoOpenCodeConfig({ config, entries, previouslyOwned: managed.opencode ?? [] }),
        onOwned: (owned) => {
          nextManaged.opencode = owned;
        },
      }),
    );
  }

  // --- Codex (~/.codex/config.toml) ---
  if (selected.includes("codex")) {
    providers.push(
      await applyToProvider({
        provider: "codex",
        configPath: path.join(home, ".codex", "config.toml"),
        mcpKey: "mcp_servers",
        previouslyOwned: managed.codex ?? [],
        dryRun: options.dryRun,
        readConfig: readTomlObject,
        writeConfig: atomicWriteToml,
        buildEntries: () => {
          // Land every server. Unresolved {env:VAR} placeholders are written as
          // literals and surface as failed until the user sets the variable.
          const entries: Record<string, CodexMcpEntry> = {};
          for (const [name, server] of Object.entries(vault.servers)) {
            entries[name] = toCodexMcpEntry(name, server, env);
          }
          return { entries, skipped: [] };
        },
        merge: (config, entries) =>
          mergeIntoCodexConfig({ config, entries, previouslyOwned: managed.codex ?? [] }),
        onOwned: (owned) => {
          nextManaged.codex = owned;
        },
      }),
    );
  }

  if (!options.dryRun) {
    await atomicWriteJson(managedStorePath, nextManaged);
  }

  return { vault, providers };
}

interface ApplyToProviderArgs<TEntry> {
  provider: "claude" | "opencode" | "codex";
  configPath: string;
  mcpKey: string;
  previouslyOwned: string[];
  dryRun: boolean;
  readConfig: (configPath: string) => Promise<Record<string, unknown> | null>;
  writeConfig: (configPath: string, value: Record<string, unknown>) => Promise<void>;
  buildEntries: () => {
    entries: Record<string, TEntry>;
    skipped: { name: string; reason: string }[];
  };
  merge: (
    config: Record<string, unknown>,
    entries: Record<string, TEntry>,
  ) => { config: Record<string, unknown>; owned: string[] };
  onOwned: (owned: string[]) => void;
}

async function applyToProvider<TEntry>(
  args: ApplyToProviderArgs<TEntry>,
): Promise<ProviderApplyResult> {
  const base: ProviderApplyResult = {
    provider: args.provider,
    configPath: args.configPath,
    detected: false,
    added: [],
    updated: [],
    removed: [],
    skipped: [],
    wrote: false,
  };
  let config: Record<string, unknown> | null;
  try {
    config = await args.readConfig(args.configPath);
  } catch (error) {
    return { ...base, error: `could not read config: ${String(error)}` };
  }
  if (config === null) {
    return base; // provider not installed (no config file)
  }
  base.detected = true;

  const { entries, skipped } = args.buildEntries();
  base.skipped = skipped;
  const diff = diffSection(sectionOf(config, args.mcpKey), entries, args.previouslyOwned);
  base.added = diff.added;
  base.updated = diff.updated;
  base.removed = diff.removed;

  const merged = args.merge(config, entries);
  args.onOwned(merged.owned);

  const hasChanges = diff.added.length + diff.updated.length + diff.removed.length > 0;
  if (!args.dryRun && hasChanges) {
    await args.writeConfig(args.configPath, merged.config);
    base.wrote = true;
  }
  return base;
}
